import { useCallback, useRef } from 'react';
import { serviceItems } from '../content';
import { petAssetById } from '../petAssets';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { PINNED_TRACK_LAYOUT, useHorizontalTrack } from '../hooks/useHorizontalTrack';

export function ServicesSection() {
  const cueRef = useRef<HTMLParagraphElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleProgress = useCallback((progress: number) => {
    if (cueRef.current) {
      cueRef.current.style.opacity = String(Math.max(0, 1 - progress * 3));
    }
  }, []);

  const { sectionRef, trackRef } = useHorizontalTrack<HTMLElement>({
    enabled: !prefersReducedMotion,
    overshoot: 64,
    scrub: 0.7,
    tail: 0.4,
    onProgress: handleProgress,
  });

  return (
    <section
      id="services"
      ref={sectionRef}
      // Pinned, the section is exactly one screen: the heading clears the
      // sticky banner and header, the rail takes whatever height is left above
      // the call dock, and nothing a visitor scrubs through sits under either.
      className={`relative overflow-hidden bg-white text-ink ${prefersReducedMotion ? '' : PINNED_TRACK_LAYOUT}`}
      aria-labelledby="services-heading"
    >
      <div className={`px-[var(--section-padding-x)] ${prefersReducedMotion ? 'pt-[var(--section-padding-y)]' : ''}`}>
        <p className="section-eyebrow">Services</p>
        <h2 id="services-heading" className="section-title">
          What the room is built for
        </h2>
        <p className="section-copy max-w-lg">
          Consults, prevention, surgery, imaging, and breeding support, under one roof.
        </p>
      </div>

      {/* The cue reads ahead of the rail rather than after it: below the cards
          it spends the whole pin behind the fixed call dock on a phone. */}
      {!prefersReducedMotion ? (
        <p
          ref={cueRef}
          className="pointer-events-none mt-6 hidden px-[var(--section-padding-x)] font-body text-xs uppercase tracking-[0.28em] text-neutral-500 lg:block"
          aria-hidden="true"
        >
          Scroll to explore services
        </p>
      ) : null}

      <div className={prefersReducedMotion ? 'pb-[var(--section-padding-y)] pt-8' : 'min-h-0 flex-1 pt-6'}>
        <div
          ref={trackRef}
          className={`flex px-[var(--section-padding-x)] ${
            prefersReducedMotion ? 'flex-col gap-8' : 'h-full w-max gap-5'
          }`}
        >
          {serviceItems.map((service, index) => (
            <article
              key={service.title}
              className={`group relative shrink-0 overflow-hidden bg-stage text-mist ${
                prefersReducedMotion
                  ? 'w-full'
                  : 'flex h-full max-h-[36rem] w-[min(82vw,26rem)] flex-col justify-end lg:w-[30rem]'
              }`}
            >
              <div className="stage-frame-inner" aria-hidden="true" />
              <img
                src={petAssetById[service.petId].srcSm}
                alt=""
                width={petAssetById[service.petId].width}
                height={petAssetById[service.petId].height}
                className="pointer-events-none absolute bottom-[-6%] right-[-8%] z-[1] h-[72%] w-auto object-contain opacity-90 transition-transform duration-300 ease-in-out will-change-transform group-hover:-translate-y-3 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
              <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-t from-stage via-stage/70 to-transparent" />
              <div className="film-grain z-[3]" aria-hidden="true" />
              <div className="relative z-[4] p-8">
                <span className="font-body text-xs uppercase tracking-[0.28em] text-dawn">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-4 font-display text-3xl font-semibold leading-[1.12] tracking-tight sm:text-4xl">
                  {service.title}
                </h3>
                <p className="mt-4 max-w-sm font-body text-[1.0625rem] leading-7 text-white/70">{service.body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
